import React from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  List,
  ListItem,
  ListItemText,
  Divider,
  Alert,
  Button
} from '@mui/material';
import { Security, Delete, Visibility, Lock, Home } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useLanguageContext } from '../contexts/LanguageContext';

const PrivacyPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useLanguageContext();

  const handleGoHome = () => {
    navigate('/');
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Paper
        elevation={0}
        sx={{
          p: 4,
          borderRadius: 2,
          backgroundColor: 'transparent',
          color: 'white',
          '& .MuiTypography-root': { color: 'white' },
          '& .MuiAlert-root .MuiTypography-root': { color: 'inherit' },
          '& .MuiDivider-root': { borderColor: 'rgba(255, 255, 255, 0.3)' }
        }}
      >
        <Typography variant="h3" component="h1" gutterBottom align="center" color="primary">
          {t('privacy.title')}
        </Typography>
        
        <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 4 }}>
          {t('privacy.effectiveDate')}
        </Typography>

        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
          <Button
            variant="outlined"
            startIcon={<Home />}
            onClick={handleGoHome}
            sx={{
              borderRadius: 2,
              px: 3,
              py: 1,
              borderColor: 'white',
              color: 'white',
              '&:hover': {
                backgroundColor: 'white',
                color: '#6F4E37',
                borderColor: 'white'
              }
            }}
          >
            {t('privacy.goHome')}
          </Button>
        </Box>

        <Alert severity="info" icon={<Lock />} sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom>
            🔒 개인정보 보호 안내
          </Typography>
          <Typography variant="body2" component="div">
            • 기도 내용의 사생활 보호를 위해 별도의 로그인이 필요하지 않습니다.<br/>
            • 성별 구분은 오직 예배 준비(성경말씀, 축도 등)를 위해서만 사용됩니다.<br/>
            • 모든 정보는 예배 종료 후 자동으로 삭제됩니다.
          </Typography>
        </Alert>

        <Divider sx={{ mb: 3 }} />

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom color="primary">
            제1조 (개인정보의 처리목적)
          </Typography>
          <Typography variant="body1" paragraph>
            고도화 사회 이니셔티브(이하 "회사")는 다음의 목적을 위하여 개인정보를 처리합니다. 처리하고 있는 개인정보는 다음의 목적 이외의 용도로는 이용되지 않으며, 이용 목적이 변경되는 경우에는 개인정보보호법 제18조에 따라 별도의 동의를 받는 등 필요한 조치를 이행합니다.
          </Typography>
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2" fontWeight="bold">
              YouTube API Services 이용 안내: 본 서비스는 YouTube API Services를 사용하여 찬송가 검색 및 재생 기능을 제공합니다. YouTube 관련 데이터 처리는 Google 개인정보처리방침(http://www.google.com/policies/privacy)을 따릅니다.
            </Typography>
          </Alert>
          <List>
            <ListItem>
              <Security sx={{ mr: 2, color: 'primary.main' }} />
              <ListItemText 
                primary="예배 서비스 제공"
                secondary="성별에 맞는 성경 구절·축도 제공과 예배 개인화를 위한 최소한의 정보 수집"
              />
            </ListItem>
            <ListItem>
              <Visibility sx={{ mr: 2, color: 'primary.main' }} />
              <ListItemText 
                primary="서비스 개선"
                secondary="익명화된 통계 정보를 통한 서비스 품질 향상"
              />
            </ListItem>
          </List>
        </Box>

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom color="primary">
            제2조 (개인정보의 처리 및 보유기간)
          </Typography>
          <Alert severity="success" icon={<Delete />} sx={{ mb: 2 }}>
              <Typography variant="body1" fontWeight="bold">
                임시 처리 원칙: 모든 개인정보는 예배 종료 즉시 자동 삭제됩니다.
              </Typography>
          </Alert>
          <Typography variant="body1" paragraph>
            1. 회사는 법령에 따른 개인정보 보유·이용기간 또는 정보주체로부터 개인정보를 수집 시에 동의받은 개인정보 보유·이용기간 내에서 개인정보를 처리·보유합니다.
          </Typography>
          <Typography variant="body1" paragraph>
            2. 본 서비스의 특성상 다음과 같이 처리됩니다:
          </Typography>
          <List>
            <ListItem>
              <ListItemText 
                primary="성별 정보"
                secondary="예배 준비 시에만 임시 사용 후 즉시 삭제"
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="기도 내용"
                secondary="서버에 저장되지 않으며, 브라우저 세션 종료 시 완전 삭제"
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="예배 관련 설정"
                secondary="예배 종료 후 자동 삭제"
              />
            </ListItem>
          </List>
        </Box>

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom color="primary">
            제3조 (처리하는 개인정보의 항목)
          </Typography>
          <Typography variant="body1" paragraph>
            회사는 최소한의 개인정보만을 수집합니다:
          </Typography>
          <List>
            <ListItem>
              <ListItemText 
                primary="선택사항"
                secondary="성별 (예배 준비를 위한 성경 구절 및 축도 개인화 목적)"
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="자동 수집 정보"
                secondary="서비스 이용 기록, 접속 로그(통계 목적, 개인 식별 불가). 쿠키는 서비스 품질 향상 및 보안 목적에 한해 최소 범위에서 사용될 수 있습니다."
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="YouTube API 관련 데이터"
                secondary="YouTube 찬송가 검색 및 재생을 위한 API 호출 정보, 동영상 메타데이터 (제목, 썸네일, 재생시간 등). 이 정보는 예배 서비스 제공 목적으로만 사용되며 예배 종료 시 삭제됩니다."
              />
            </ListItem>
          </List>
          <Alert severity="warning" sx={{ mt: 2 }}>
            <Typography variant="body2">
              주의: 이름, 전화번호, 이메일 등의 개인식별정보는 원칙적으로 수집하지 않습니다. 외부 결제 과정에서 결제사가 수집하는 정보는 해당 결제사 정책이 적용됩니다.
            </Typography>
          </Alert>
        </Box>

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom color="primary">
            제3-2조 (정보의 사용 및 공유)
          </Typography>
          <Typography variant="body1" paragraph>
            회사는 수집된 정보를 다음과 같이 사용하고 공유합니다:
          </Typography>
          <List>
            <ListItem>
              <ListItemText 
                primary="내부 사용"
                secondary="예배 서비스 개인화, 서비스 품질 향상, 통계 분석 (개인 식별 불가 형태)"
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="외부 공유"
                secondary="YouTube API Services를 통한 동영상 검색 및 재생, 결제 처리를 위한 결제대행사와의 정보 공유 (최소 필요 범위)"
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="제3자 광고 및 콘텐츠"
                secondary="YouTube 임베드 동영상을 통해 제3자 광고가 표시될 수 있으며, 이는 Google/YouTube의 광고 정책에 따라 운영됩니다. 당사는 이러한 제3자 광고 콘텐츠를 직접 제어하지 않습니다."
              />
            </ListItem>
          </List>
        </Box>

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom color="primary">
            제3-3조 (쿠키 및 기기 정보)
          </Typography>
          <Typography variant="body1" paragraph>
            본 서비스는 다음과 같은 방식으로 쿠키 및 기기 정보를 수집하고 사용할 수 있습니다:
          </Typography>
          <List>
            <ListItem>
              <ListItemText 
                primary="쿠키 사용"
                secondary="서비스 품질 향상, 사용자 선호도 기억, 보안 강화 목적. 브라우저 설정을 통해 쿠키를 차단할 수 있으나 일부 기능이 제한될 수 있습니다."
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="기기 정보"
                secondary="브라우저 종류, 운영체제, 화면 해상도 등 기술적 정보 (개인 식별 불가). 서비스 최적화 및 오류 해결 목적으로 사용됩니다."
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="제3자 쿠키"
                secondary="YouTube 임베드 동영상, 결제 시스템 등 외부 서비스 이용 시 해당 서비스 제공자의 쿠키가 설정될 수 있으며, 이는 각 제공자의 정책을 따릅니다."
              />
            </ListItem>
          </List>
        </Box>

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom color="primary">
            제4조 (개인정보의 제3자 제공)
          </Typography>
          <Typography variant="body1" paragraph>
            회사는 정보주체의 개인정보를 제1조(개인정보의 처리목적)에서 명시한 범위 내에서만 처리하며, 정보주체의 동의 또는 법률의 특별한 규정 등 개인정보보호법 제17조 및 제18조에 해당하는 경우에만 개인정보를 제3자에게 제공합니다.
          </Typography>
          <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2" fontWeight="bold">
                현재 제3자에게 개인정보를 제공하지 않습니다. 다만, 다음의 경우 필요한 범위 내에서 정보가 전송될 수 있습니다:
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                • YouTube API Services: 찬송가 검색 및 재생을 위한 API 호출 정보<br/>
                • 결제 처리: 결제대행사(토스페이먼츠 등)와의 거래 정보 공유<br/>
                • Google 서비스: YouTube 임베드 시 Google 개인정보처리방침 적용<br/>
                이 경우 각 제공자의 개인정보 처리방침이 적용됩니다.
              </Typography>
          </Alert>
        </Box>

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom color="primary">
            제5조 (개인정보의 파기)
          </Typography>
          <Typography variant="body1" paragraph>
            회사는 개인정보 보유기간의 경과, 처리목적 달성 등 개인정보가 불필요하게 되었을 때에는 지체 없이 해당 개인정보를 파기합니다.
          </Typography>
          <Typography variant="body1" paragraph>
            <strong>파기절차 및 방법:</strong>
          </Typography>
          <List>
            <ListItem>
              <ListItemText 
                primary="자동 파기"
                secondary="예배 종료 시 시스템에서 자동으로 모든 임시 데이터 삭제"
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="세션 종료"
                secondary="브라우저 세션 종료 시 모든 로컬 데이터 자동 삭제"
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="복구 불가"
                secondary="삭제된 정보는 기술적으로 복구가 불가능하도록 처리"
              />
            </ListItem>
          </List>
        </Box>

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom color="primary">
            제6조 (정보주체와 법정대리인의 권리·의무 및 행사방법)
          </Typography>
          <Typography variant="body1" paragraph>
            정보주체는 회사에 대해 언제든지 다음 각 호의 개인정보 보호 관련 권리를 행사할 수 있습니다:
          </Typography>
          <List>
            <ListItem>
              <ListItemText 
                primary="1. 개인정보 처리현황 통지요구"
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="2. 오류 등이 있을 경우 정정·삭제 요구"
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="3. 처리정지 요구"
              />
            </ListItem>
          </List>
          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2">
              본 서비스는 개인정보를 저장하지 않으므로, 대부분의 권리는 자동으로 보장됩니다.
            </Typography>
          </Alert>
        </Box>

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom color="primary">
            제7조 (개인정보 보호책임자)
          </Typography>
          <Typography variant="body1" paragraph>
            회사는 개인정보 처리에 관한 업무를 총괄해서 책임지고, 개인정보 처리와 관련한 정보주체의 불만처리 및 피해구제 등을 위하여 아래와 같이 개인정보 보호책임자를 지정하고 있습니다.
          </Typography>
          <Paper elevation={0} sx={{ bgcolor: 'transparent', border: '1px solid rgba(255, 255, 255, 0.4)', p: 2, mt: 2 }}>
            <Typography variant="h6" gutterBottom>
              개인정보 보호책임자
            </Typography>
            <Typography variant="body2">성명: 공훈의</Typography>
            <Typography variant="body2">직책: 대표이사</Typography>
            <Typography variant="body2">연락처: 070-8621-3108</Typography>
            <Typography variant="body2">주소: 서울시 종로구 평창21길 9-26 삼형파크 A동 102호</Typography>
          </Paper>
        </Box>

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom color="primary">
            제8조 (개인정보의 안전성 확보조치)
          </Typography>
          <Typography variant="body1" paragraph>
            회사는 개인정보의 안전성 확보를 위해 다음과 같은 조치를 취하고 있습니다:
          </Typography>
          <List>
            <ListItem>
              <ListItemText 
                primary="기술적 조치"
                secondary="개인정보처리시스템 접근권한 관리, 접근통제시스템, 전송구간 암호화(HTTPS), 고유식별정보 등의 암호화"
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="물리적 조치"
                secondary="전산실, 자료보관실 등의 접근통제"
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="관리적 조치"
                secondary="개인정보취급자의 최소화 및 교육, 정기 점검"
              />
            </ListItem>
          </List>
        </Box>

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom color="primary">
            제9조 (개인정보처리방침의 변경)
          </Typography>
          <Typography variant="body1" paragraph>
            1. 이 개인정보처리방침은 시행일로부터 적용되며, 법령 및 방침 변경에 따른 추가·삭제·정정이 있는 경우에는 변경사항의 시행 7일 전부터 공지사항을 통하여 고지합니다.
          </Typography>
          <Typography variant="body1" paragraph>
            2. 본 방침은 2025년 1월 10일부터 시행됩니다.
          </Typography>
        </Box>

        <Divider sx={{ my: 3 }} />

        <Box sx={{ textAlign: 'center', mt: 4 }}>
          <Button
            variant="contained"
            startIcon={<Home />}
            onClick={handleGoHome}
            sx={{
              mb: 3,
              borderRadius: 2,
              px: 4,
              py: 1.5,
              background: '#6F4E37',
              '&:hover': {
                background: '#5A3E2B',
                transform: 'translateY(-1px)',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)'
              },
              transition: 'all 0.3s ease'
            }}
          >
            홈으로 가기
          </Button>
          <Typography variant="body2" color="text.secondary">
            개인정보 관련 문의사항이 있으시면 고도화 사회 이니셔티브 (huney.kong@gmail.com)로 연락주시기 바랍니다.
          </Typography>
        </Box>
      </Paper>
    </Container>
  );
};

export default PrivacyPage;